// The OUTLINE border, drawn as traced GEOMETRY: the one territory border
// of the map (R495, R532).
//
// This pair of programs draws the border from the outline the CPU traces in
// `../outline-geo.ts`:
//
//  - `GEO_FILL_*` draws the sovereignty wash. The CPU triangulates each
//    owner region, so the fragment states one alpha and no distance at all.
//  - `GEO_STROKE_*` draws the glow border. The CPU emits a STRIP of the
//    outline, two vertices a point, offset along the miter normal; the
//    vertex shader pushes each vertex to the half width and hands the
//    fragment a signed distance to the line. The fragment then composes the
//    three passes of `[presentation.territory] border_passes`.
//
// **The line pass carries the faction's dash** (R1568, R1577). Every strip
// vertex carries its length along the loop and the length of the loop, so
// the pattern runs along the real line through every corner. The vertex shader
// stretches each loop to a whole number of the faction's periods, so the
// pattern ends where it began. The halo and the glow stay solid. The
// arithmetic is `DASH_ALONG` of `territory.ts`.
//
// **The strip half width is a uniform and not baked into the vertices.**
// The mesh carries a unit-scale normal, so a zoom, a `#bw` change or a
// device-pixel-ratio change moves the vertices in the vertex shader and
// costs no rebuild of the buffer. `uAaPad` is the room the box filter
// needs: `lineCov` samples one device pixel across, so the strip must stand
// one device pixel (measured in world px) wider than the widest pass, or
// the outermost glow is cut off at the strip's own edge.
//
// **Each glow sits a little inside its own claim.** Two factions that share
// a border trace the same line, and two glows on one line cover each
// other. `uInset` moves the strip along the loop's inward normal, so each
// faction's glow lands on its own side and both stay visible (R497). The
// signed distance still counts from the moved line, so the passes keep
// their widths.
//
// **Both programs fade with the arrival** (R838, R1578). A spawn style that
// fades the territory in writes `uArrival`, and the wash and the glow each
// multiply the last colour they write by it.
// At the steady state 1 the product is the colour itself, to the bit.
//
// **The wash is cut by the terrain plane's rock mask**, through
// `ROCK_PLANE` of `territory.ts`, so the tint stays on water (R103). **The
// glow is not cut** (R496): the outline runs round a ridge on a coast
// (R498), and the owner wants that line drawn over the mountain.

import {
  TERRITORY_BORDER_PASSES, TERRITORY_GEO_INSET_PX, TERRITORY_TINT_ALPHA,
} from '../../gen/presentation';
import { PRELUDE, SHARED_UNIFORMS, type UniformSpec } from './prelude';
import { worldProgram } from '../gfx/worldquad';
import type { AttribSpec } from '../gfx/types';
import type { ProgramSource } from './program';
import {
  ARRIVAL_UNIFORMS, BORDER_PASS_UNIFORMS, DASH_ALONG, DASH_UNIFORMS, ROCK_PLANE,
  ROCK_PLANE_UNIFORMS,
} from './territory';
import {
  GEO_FILL_VERTEX_WGSL, GEO_STROKE_VARYINGS_WGSL, GEO_STROKE_VERTEX_WGSL,
  geoFillBodyWgsl, geoStrokeBodyWgsl,
} from './wgsl/geo';

/**
 * The strip's own width before the aa pad: the widest of the three passes.
 *
 * A narrower strip would cut the outermost glow, and a wider one would pay
 * fill for pixels every pass reads as empty.
 */
const STRIP_WIDTH = Math.max(...TERRITORY_BORDER_PASSES.map((p) => p.width));

// ---------------------------------------------------------------------------
// The fill
// ---------------------------------------------------------------------------

/** The fill's vertex shader: one world point a vertex, and no more. */
export const GEO_FILL_VERTEX = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
uniform mat3 uViewProj;
out vec2 vWorld;
void main() {
  vWorld = aPos;
  gl_Position = vec4((uViewProj * vec3(aPos, 1.0)).xy, 0.0, 1.0);
}`;

const FILL_BODY = `
uniform int   uFaction;
uniform float uTintAlpha;
uniform float uArrival;

void main() {
  float px = worldPx();
  float water = rockPlaneWater(vWorld, px);
  float a = uTintAlpha * water;
  fragColor = vec4(uPalette[uFaction].rgb * a, a) * uArrival;
}
`;

/** The fill's fragment shader, prelude and rock mask included. */
export const GEO_FILL_FRAG = PRELUDE + ROCK_PLANE + FILL_BODY;

// ---------------------------------------------------------------------------
// The stroke
// ---------------------------------------------------------------------------

/**
 * The stroke's vertex shader.
 *
 * `aNormal` is the unit-scale miter normal the trace computed and `aSide` is
 * which edge of the strip this vertex is, so the half width is applied here
 * and the fragment reads a signed distance to the outline in world px.
 *
 * `aArc` is the length along the loop from its first point (x) and the
 * length of the whole loop (y), in world px (R1577). The shader stretches
 * the loop to a whole number of the faction's dash periods, at least one,
 * and hands the fragment the place along the line in pattern units. The
 * stretch is the same for every vertex of one loop, so the place runs on
 * evenly, and the strip's last pair (the first point at the whole length)
 * reads the pattern at the phase of the first pair. A solid faction has a
 * period of 0 and takes no stretch.
 */
export const GEO_STROKE_VERTEX = `#version 300 es
precision highp float;
precision highp int;
layout(location = 0) in vec2 aPos;
layout(location = 1) in vec2 aNormal;
layout(location = 2) in float aSide;
layout(location = 3) in vec2 aArc;
uniform mat3 uViewProj;
uniform float uStripWidth;
uniform float uWidthScale;
uniform float uAaPad;
uniform float uInset;
uniform int   uFaction;
uniform vec4  uDashB[8];
out vec2 vWorld;
out float vD;
out float vS;
void main() {
  // \`half\` is a reserved word in GLSL ES, so the name stays halfW.
  float halfW = 0.5 * uStripWidth * uWidthScale + uAaPad;
  // aNormal is signed by the side, so aNormal * aSide is the inward normal
  // on both edges of the strip, and the inset moves the whole strip in.
  vec2 p = aPos + aNormal * (halfW + aSide * uInset);
  vD = aSide * halfW;
  // The dash of R1568 runs along the loop (R1577): a whole number of the
  // faction's periods fills the loop, so the pattern closes on itself.
  float period = uDashB[uFaction].z * uWidthScale;
  float loopLen = max(aArc.y, 1e-3);
  float fit = (period > 0.0)
    ? max(floor(loopLen / period + 0.5), 1.0) * period / loopLen : 1.0;
  vS = aArc.x * fit / uWidthScale;
  // The prelude declares vWorld for every fragment; the stroke reads no
  // world point, but the varying must still be fed.
  vWorld = p;
  gl_Position = vec4((uViewProj * vec3(p, 1.0)).xy, 0.0, 1.0);
}`;

const STROKE_BODY = `
in float vD;
in float vS;

uniform int   uFaction;
uniform vec3  uPassWidth;
uniform vec3  uPassAlpha;
uniform float uPulse;
uniform float uArrival;
uniform float uWidthScale;
uniform int   uEdgeDash;
uniform vec4  uDashA[8];
uniform vec4  uDashB[8];
${DASH_ALONG}
void main() {
  float px = worldPx();
  float d = abs(vD);
  // The LINE pass takes the faction's dash (R1568, R1577): the distance to
  // the nearest run along the loop and the distance to the line make a
  // round-capped dash. The halo and the glow stay solid. It is the third
  // pass, because border_passes is three passes, the line last.
  float dl = length(vec2(dashAlong(uFaction, vS), d));
  // Three overlaid same-colour strokes, composed as \`territoryPaint\` does:
  // a sum would pass 1 at the centre, so the passes stack as
  // 1 - prod(1 - a*c).
  float keep = 1.0;
  for (int q = 0; q < 3; q++) {
    float dq = (q == 2) ? dl : d;
    keep *= 1.0 - uPassAlpha[q] * lineCov(dq, 0.5 * uPassWidth[q] * uWidthScale, px);
  }
  float a = (1.0 - keep) * uPulse;
  // The arrival fades the glow by the factor that fades the wash (R1578).
  fragColor = vec4(uPalette[uFaction].rgb * a, a) * uArrival;
}
`;

/** The stroke's fragment shader, prelude and rock mask included. */
export const GEO_STROKE_FRAG = PRELUDE + STROKE_BODY;

// ---------------------------------------------------------------------------
// The uniform lists
// ---------------------------------------------------------------------------

/**
 * The fill's own uniforms, on top of `SHARED_UNIFORMS`.
 *
 * `uFaction` is dynamic because one draw is one region of one faction: the
 * layer writes it between draws, and the value here is only a safe start.
 */
export const GEO_FILL_UNIFORMS: readonly UniformSpec[] = [
  { name: 'uFaction', type: 'int', value: 0, dynamic: true },
  { name: 'uTintAlpha', type: 'float', value: TERRITORY_TINT_ALPHA },
  ...ARRIVAL_UNIFORMS,
  ...ROCK_PLANE_UNIFORMS,
];

/**
 * The stroke's own uniforms, on top of `SHARED_UNIFORMS`.
 *
 * The dash list is `DASH_UNIFORMS` of `territory.ts` (R1577), and the
 * arrival's alpha is `ARRIVAL_UNIFORMS` (R1578).
 */
export const GEO_STROKE_UNIFORMS: readonly UniformSpec[] = [
  { name: 'uFaction', type: 'int', value: 0, dynamic: true },
  ...BORDER_PASS_UNIFORMS,
  ...ARRIVAL_UNIFORMS,
  ...DASH_UNIFORMS,
  { name: 'uWidthScale', type: 'float', value: 1 },
  { name: 'uStripWidth', type: 'float', value: STRIP_WIDTH, note: 'the widest border pass' },
  { name: 'uAaPad', type: 'float', value: 1, dynamic: true, note: 'one device px in world px' },
  { name: 'uInset', type: 'float', value: TERRITORY_GEO_INSET_PX, note: 'vertex only' },
];

/** Every uniform the fill program declares. */
export const GEO_FILL_ALL_UNIFORMS: readonly UniformSpec[] = [
  ...SHARED_UNIFORMS, ...GEO_FILL_UNIFORMS,
];

/** Every uniform the stroke program declares. */
export const GEO_STROKE_ALL_UNIFORMS: readonly UniformSpec[] = [
  ...SHARED_UNIFORMS, ...GEO_STROKE_UNIFORMS,
];

/**
 * The fill mesh's one attribute: the world point (`../outline-geo.ts`).
 *
 * The two tables state the layout `outline-geo.ts` writes, and the two
 * vertex sources above declare the same locations. They live beside the
 * sources so one file states both halves.
 */
export const GEO_FILL_ATTRIBS: readonly AttribSpec[] = [
  { name: 'aPos', location: 0, size: 2, offset: 0 },
];

/**
 * The stroke mesh's four: the point, the miter normal, the side, and the
 * length along the loop beside the length of the loop (R1577).
 */
export const GEO_STROKE_ATTRIBS: readonly AttribSpec[] = [
  { name: 'aPos', location: 0, size: 2, offset: 0 },
  { name: 'aNormal', location: 1, size: 2, offset: 2 },
  { name: 'aSide', location: 2, size: 1, offset: 4 },
  { name: 'aArc', location: 3, size: 2, offset: 5 },
];

/** The outline fill, as both backends read it. */
export function geoFillProgram(): ProgramSource {
  return worldProgram({
    name: 'territory-geo-fill',
    vertex: GEO_FILL_VERTEX,
    fragment: GEO_FILL_FRAG,
    raw: true,
    uniforms: GEO_FILL_ALL_UNIFORMS,
    attribs: GEO_FILL_ATTRIBS,
    // The twin writes the BODY and a vertex entry of its own; the header and
    // the input struct come from the block `worldProgram` assembled.
    wgslBody: geoFillBodyWgsl(),
    wgslVertex: GEO_FILL_VERTEX_WGSL,
  });
}

/** The outline stroke, as both backends read it. */
export function geoStrokeProgram(): ProgramSource {
  return worldProgram({
    name: 'territory-geo-stroke',
    vertex: GEO_STROKE_VERTEX,
    fragment: GEO_STROKE_FRAG,
    raw: true,
    uniforms: GEO_STROKE_ALL_UNIFORMS,
    attribs: GEO_STROKE_ATTRIBS,
    // The stroke carries a varying of its own, so it hands the assembly rule
    // its whole varying struct beside the body and the vertex entry.
    wgslBody: geoStrokeBodyWgsl(),
    wgslVertex: GEO_STROKE_VERTEX_WGSL,
    wgslVaryings: GEO_STROKE_VARYINGS_WGSL,
  });
}
